'use client'

import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useRouter, usePathname } from 'next/navigation'
import Image from 'next/image'
import { ArrowRight, Calendar, Check, ChevronDown, CircleCheck, Copy, ExternalLink, Gift, Mail, MessageSquare, Printer, Send, ShieldCheck, ShoppingBag, Sparkles, XCircle, Zap } from 'lucide-react'
import QRCodeImport from 'react-qr-code'
import qpayLogo from '@/assets/qpay.svg'
import { toast } from '@/lib/toast'
import { useGiftCardPaymentChecker, useInAppBrowser, IN_APP_BROWSER_LABELS } from '@/lib/commerce/hooks'
import { STATUS_TONES, STATUS_LABELS } from '@/utils/gift'
import { navigateUrl } from '@/utils/client-request'
import { formatDateTimeLong, formatImageURL, formatPrice } from '@/utils/util'

const QRCode = QRCodeImport as unknown as React.FC<{ value: string, size?: number, fgColor?: string, bgColor?: string, style?: React.CSSProperties }>

export default function GiftDetail({ giftCard, vendor }: { giftCard: any, vendor?: any }) {
  const router = useRouter()
  const pathname = usePathname() || ''
  const status = giftCard?.status
  const tone = STATUS_TONES[status] || STATUS_TONES.pending
  const label = STATUS_LABELS[status] || 'Тодорхойгүй'

  const isPending = status === 'pending'
  const isPaidLike = ['paid', 'sent', 'used'].includes(status)
  const isCanceled = status === 'canceled'

  const recipient = giftCard?.fullName || `${giftCard?.firstName || ''} ${giftCard?.lastName || ''}`.trim()

  return (
    <section className="bg-cream pt-6 pb-16 min-h-screen">
      <div className="mx-auto max-w-240 px-4 sm:px-6 lg:px-10">
        <HeroCard giftCard={giftCard} status={status} tone={tone} label={label} isPending={isPending} isPaidLike={isPaidLike} isCanceled={isCanceled} />

        {isPending && giftCard?.qpay && (
          <div className="mt-4">
            <PendingPayment giftCard={giftCard} vendor={vendor} onSuccess={() => router.refresh()} />
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-[1.3fr_1fr] gap-4 mt-4">
          {/* Card preview */}
          <div className="bg-white rounded-3xl p-6 lg:p-7">
            <h3 className="text-[20px] font-bold font-gilroy tracking-[-0.015em] text-foreground mb-5 inline-flex items-center gap-2">
              <Gift className="size-4.5" /> Бэлгийн карт
            </h3>
            <div className="relative aspect-3/2 w-full rounded-2xl overflow-hidden bg-foreground text-white">
              {giftCard?.template?.thumbnail && (
                <img src={formatImageURL(giftCard.template.thumbnail)} alt={giftCard?.template?.name} className="absolute inset-0 w-full h-full object-cover" />
              )}
              <div className="absolute inset-0 bg-linear-to-br from-black/20 to-black/55" />
              <div className="absolute inset-0 flex flex-col justify-between p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-[10px] font-semibold tracking-[0.22em] uppercase opacity-80">Бэлгийн карт</div>
                    <div className="text-[15px] font-bold mt-1 font-gilroy">{vendor?.name || 'Shop.mn'}</div>
                  </div>
                  {giftCard?.template?.name && (
                    <div className="text-[10px] font-semibold tracking-[0.18em] uppercase opacity-80">{giftCard.template.name}</div>
                  )}
                </div>
                <div className="flex-1 flex items-start min-w-0 pt-2">
                  {giftCard?.message ? (
                    <div className="text-[12.5px] opacity-90 italic line-clamp-3 leading-relaxed">"{giftCard.message}"</div>
                  ) : (
                    <div className="text-[11px] opacity-50">Зурвас бичээгүй</div>
                  )}
                </div>
                <div className="flex justify-end">
                  <div className="text-right">
                    <div className="font-bold font-gilroy tracking-[-0.025em] leading-none tabular-nums" style={{ fontSize: 'clamp(2.25rem, 4vw, 3rem)' }}>{formatPrice(giftCard?.amount)}</div>
                    {recipient && <div className="text-[12px] opacity-85 mt-2">{recipient}-д</div>}
                  </div>
                </div>
              </div>
            </div>

            {giftCard?.code && isPaidLike && (
              <div className="mt-4 p-4 rounded-2xl bg-[#F1EEE7] flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-[11px] font-semibold tracking-[0.16em] uppercase text-foreground/55 mb-1">Картын код</div>
                  <div className="text-[18px] font-bold font-mono tracking-wider text-foreground">{giftCard.code}</div>
                </div>
                <button
                  onClick={() => { navigator.clipboard?.writeText(giftCard.code); toast.success('Картын код хуулагдлаа') }}
                  className="inline-flex items-center gap-2 h-10 px-4 rounded-full bg-foreground text-cream text-[12.5px] font-semibold hover:bg-foreground/90 transition-colors cursor-pointer shrink-0"
                >
                  <Copy className="size-3.5" /> Хуулах
                </button>
              </div>
            )}
          </div>

          {/* Details + breakdown */}
          <div className="bg-white rounded-3xl p-6 lg:p-7">
            <h3 className="text-[20px] font-bold font-gilroy tracking-[-0.015em] text-foreground mb-4">Картын мэдээлэл</h3>
            <ul className="divide-y divide-foreground/8">
              <Row icon={Gift} label="Дизайн" value={giftCard?.template?.name || '—'} />
              <Row icon={Mail} label="Хүлээн авагч" value={recipient || '—'} />
              {giftCard?.email && <Row icon={Mail} label="Хүлээн авагчийн имэйл" value={giftCard.email} />}
              {giftCard?.senderEmail && <Row icon={Send} label="Илгээгчийн имэйл" value={giftCard.senderEmail} />}
              {giftCard?.message && <Row icon={MessageSquare} label="Зурвас" value={giftCard.message} />}
              {giftCard?.deliveryDate && <Row icon={Calendar} label="Илгээх огноо" value={formatDateTimeLong(giftCard.deliveryDate)} />}
              {giftCard?.paidAt && <Row icon={CircleCheck} label="Төлсөн" value={formatDateTimeLong(giftCard.paidAt)} />}
              {giftCard?.sentAt && <Row icon={Send} label="Илгээгдсэн" value={formatDateTimeLong(giftCard.sentAt)} />}
              {giftCard?.usedAt && <Row icon={Check} label="Ашиглагдсан" value={formatDateTimeLong(giftCard.usedAt)} />}
              {giftCard?.canceledAt && <Row icon={XCircle} label="Цуцлагдсан" value={formatDateTimeLong(giftCard.canceledAt)} />}
            </ul>

            <div className="mt-5 pt-5 border-t border-foreground/10 flex flex-col gap-2 text-[13.5px]">
              <div className="flex items-center justify-between">
                <span className="text-foreground/60">Картын дүн</span>
                <span className="font-medium tabular-nums text-foreground">{formatPrice(giftCard?.amount)}</span>
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-dashed border-foreground/15 flex items-baseline justify-between">
              <span className="text-[14px] font-semibold text-foreground">{isPaidLike ? 'Төлсөн' : 'Төлөх дүн'}</span>
              <span className="font-bold font-gilroy tracking-[-0.02em] text-foreground text-[26px]">{formatPrice(giftCard?.amount)}</span>
            </div>
          </div>
        </div>

        <div className="mt-4 bg-white rounded-3xl p-6 lg:p-7 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
          <div>
            <h3 className="text-[22px] font-bold font-gilroy tracking-[-0.02em] text-foreground">Шинэ карт авах</h3>
            <p className="text-[13.5px] text-foreground/60 mt-1">Найз, гэр бүлдээ бэлэг илгээгээрэй.</p>
          </div>
          <a href={navigateUrl('/gift', pathname)} className="inline-flex items-center justify-center gap-2 h-13 px-6 rounded-full bg-foreground text-cream text-[14px] font-semibold hover:bg-foreground/90 transition-colors self-start sm:self-auto">
            <ShoppingBag className="size-4" /> Бэлгийн карт авах <ArrowRight className="size-4" />
          </a>
        </div>

        <div className="mt-6 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-[12.5px] text-foreground/55 print:hidden">
          <button onClick={() => window.print()} className="inline-flex items-center gap-1.5 hover:text-foreground transition-colors cursor-pointer">
            <Printer className="size-3.5" /> Хэвлэх
          </button>
          <span className="text-foreground/20">·</span>
          <a href="https://help.shop.mn" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 hover:text-foreground transition-colors">Тусламж</a>
          <span className="text-foreground/20">·</span>
          <a href={navigateUrl('/gift', pathname)} className="inline-flex items-center gap-1.5 hover:text-foreground transition-colors">Шинэ карт авах <ArrowRight className="size-3.5" /></a>
        </div>
      </div>
      <PrintableGiftReceipt giftCard={giftCard} vendor={vendor} recipient={recipient} label={label} />
    </section>
  )
}

function PrintableGiftReceipt({ giftCard, vendor, recipient, label }: { giftCard: any, vendor: any, recipient: string, label: string }) {
  const [mounted, setMounted] = useState(false)
  useEffect(() => { setMounted(true) }, [])
  if (!mounted) return null
  return createPortal(
    <div data-print-root className="hidden print:block text-black text-[12px] leading-snug">
      <style>{'@page { size: A4; margin: 16mm; }'}</style>
      <div className="text-center mb-4">
        <div className="text-lg font-bold">{vendor?.name || 'Shop.mn'}</div>
        <div className="text-[11px]">Бэлгийн картын баримт</div>
      </div>
      <div className="flex justify-between border-y border-black py-2 mb-3">
        <div>
          <div className="font-bold">Карт #{giftCard?.customId}</div>
          {giftCard?.createdAt && <div className="text-[11px]">{formatDateTimeLong(giftCard.createdAt)}</div>}
        </div>
        <div className="text-right">
          <div className="text-[11px]">Төлөв</div>
          <div className="font-bold">{label}</div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4 mb-3">
        <div>
          <div className="text-[10px] uppercase font-bold mb-1">Хүлээн авагч</div>
          <div>{recipient || '—'}</div>
          {giftCard?.email && <div>{giftCard.email}</div>}
        </div>
        <div>
          <div className="text-[10px] uppercase font-bold mb-1">Илгээгч</div>
          <div>{giftCard?.senderEmail || '—'}</div>
          {giftCard?.deliveryDate && <div>Илгээх: {formatDateTimeLong(giftCard.deliveryDate)}</div>}
        </div>
      </div>
      {giftCard?.message && (
        <div className="mb-3">
          <div className="text-[10px] uppercase font-bold mb-1">Зурвас</div>
          <div className="italic">&ldquo;{giftCard.message}&rdquo;</div>
        </div>
      )}
      <table className="w-full border-collapse mb-3">
        <thead>
          <tr className="border-y border-black">
            <th className="text-left py-1 font-bold">Зүйл</th>
            <th className="text-right py-1 font-bold w-32">Дүн</th>
          </tr>
        </thead>
        <tbody>
          <tr className="border-b border-zinc-300 align-top">
            <td className="py-1 pr-2">Бэлгийн карт{giftCard?.template?.name ? ` · ${giftCard.template.name}` : ''}</td>
            <td className="py-1 text-right tabular-nums">{formatPrice(giftCard?.amount)}</td>
          </tr>
        </tbody>
      </table>
      <div className="ml-auto w-1/2 text-[11px]">
        <div className="flex justify-between border-t border-black mt-1 pt-1 font-bold text-[13px]">
          <span>Нийт</span>
          <span className="tabular-nums">{formatPrice(giftCard?.amount)}</span>
        </div>
      </div>
      {giftCard?.code && (
        <div className="mt-4 border border-black p-3">
          <div className="text-[10px] uppercase font-bold mb-1">Картын код</div>
          <div className="text-[16px] font-mono tracking-wider">{giftCard.code}</div>
        </div>
      )}
      <div className="mt-6 text-center text-[10px]">
        Энэхүү баримтыг {formatDateTimeLong(new Date())}-нд хэвлэв · {vendor?.name || 'Shop.mn'}
      </div>
    </div>,
    document.body,
  )
}

function HeroCard({ giftCard, status, tone, label, isPending, isPaidLike, isCanceled }: { giftCard: any, status: string, tone: any, label: string, isPending: boolean, isPaidLike: boolean, isCanceled: boolean }) {
  const isUsed = status === 'used'
  const isSent = status === 'sent'
  let icon: React.ReactNode = <Check className="size-9" strokeWidth={2.5} />
  let heading = 'Захиалга баталгаажлаа.'
  let lede: React.ReactNode = null

  if (isUsed) {
    icon = <Check className="size-9" strokeWidth={2.5} />
    heading = 'Карт ашиглагдсан.'
    lede = <>Бэлгийн карт амжилттай ашиглагдсан байна.</>
  } else if (isSent) {
    icon = <Send className="size-9" strokeWidth={2.2} />
    heading = 'Илгээгдлээ.'
    lede = <>Хүлээн авагчийн имэйл рүү бэлгийн картыг илгээлээ.</>
  } else if (isPaidLike) {
    icon = <Sparkles className="size-9" strokeWidth={2.2} />
    heading = 'Захиалга баталгаажлаа.'
    lede = <>{giftCard?.deliveryDate ? <>Илгээх өдөр ({formatDateTimeLong(giftCard.deliveryDate)}) хүлээн авагчид имэйлээр бэлгийн карт хүргэгдэнэ.</> : <>Хүлээн авагчид имэйлээр бэлгийн карт хүргэгдэнэ.</>}</>
  } else if (isPending) {
    icon = <Mail className="size-9" strokeWidth={2.2} />
    heading = 'Төлбөрөө төлнө үү.'
    lede = <>QPay-ээр <b className="text-foreground tabular-nums">{formatPrice(giftCard?.amount)}</b> төлбөр хүлээгдэж байна.</>
  } else if (isCanceled) {
    icon = <XCircle className="size-9" strokeWidth={2.2} />
    heading = 'Цуцлагдсан.'
    lede = <>Бэлгийн картын захиалга цуцлагдсан байна.</>
  }

  return (
    <div className="bg-white rounded-3xl p-7 sm:p-10 relative overflow-hidden">
      <div className="absolute -top-16 -right-12 w-60 h-60 rounded-full opacity-70 pointer-events-none" style={{ background: tone.bg }} />
      <div className="absolute -top-4 -right-2 w-16 h-16 rounded-full opacity-80 pointer-events-none" style={{ background: tone.ring }} />

      <div className="relative">
        <div className="flex flex-wrap items-center gap-5">
          <div className="w-18 h-18 rounded-full inline-flex items-center justify-center" style={{ background: tone.bg, color: tone.fg }}>
            {icon}
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-[12px] font-semibold uppercase tracking-[0.12em] text-foreground/55">
            <span>Бэлгийн карт</span>
            <button
              onClick={() => {
                if (!giftCard?.customId) return
                navigator.clipboard?.writeText(giftCard.customId)
                toast.success('Дугаар хуулагдлаа')
              }}
              className="inline-flex items-center gap-1.5 text-foreground hover:opacity-70 transition-opacity cursor-pointer tabular-nums"
            >
              #{giftCard?.customId} <Copy className="size-3" />
            </button>
            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10.5px] font-semibold normal-case tracking-normal" style={{ background: tone.bg, color: tone.fg }}>
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: tone.dot }} /> {label}
            </span>
          </div>
        </div>

        <h1 className="font-bold font-gilroy tracking-[-0.03em] leading-[0.98] text-foreground mt-5" style={{ fontSize: 'clamp(2rem, 4.5vw, 3.25rem)' }}>
          {heading}
        </h1>

        {lede && (
          <p className="mt-4 text-[15px] text-foreground/65 max-w-140 leading-relaxed">{lede}</p>
        )}
      </div>
    </div>
  )
}

function PendingPayment({ giftCard, vendor, onSuccess }: { giftCard: any, vendor: any, onSuccess: () => void }) {
  const { loading, success, check } = useGiftCardPaymentChecker({ giftCard, onSuccess })
  const { isInAppBrowser, appLinksBlocked, browserUrl, openInBrowser } = useInAppBrowser({ giftCard })

  if (success) {
    return (
      <div className="bg-white rounded-3xl p-5 inline-flex items-center gap-3 w-full">
        <div className="w-11 h-11 rounded-full bg-emerald-50 text-emerald-700 inline-flex items-center justify-center shrink-0">
          <CircleCheck className="size-6" />
        </div>
        <div>
          <div className="text-[14px] font-semibold text-foreground">Төлбөр амжилттай төлөгдлөө</div>
          <div className="text-[12px] text-foreground/55 mt-0.5">Бэлгийн карт баталгаажиж, илгээх өдөр хүлээн авагчид имэйлээр хүргэгдэнэ.</div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-3xl overflow-hidden">
      <div className="px-6 pt-5 pb-4 flex items-center gap-3 border-b border-foreground/8">
        <div className="w-11 h-11 rounded-xl bg-[#F1EEE7] inline-flex items-center justify-center shrink-0 p-2">
          <Image src={qpayLogo} alt="QPay" className="w-full h-full object-contain" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-wider text-foreground/55 font-semibold">Төлбөрийн арга</div>
          <div className="text-[15px] font-semibold text-foreground truncate">QPay <span className="text-foreground/55 font-normal">· QR төлбөр</span></div>
        </div>
        <div className="hidden sm:inline-flex items-center gap-1.5 text-[12px] text-emerald-600 font-semibold">
          <Zap className="size-3.5" /> Шуурхай
        </div>
      </div>

      <div className="grid sm:grid-cols-[auto_1fr] gap-5 p-6">
        <div className="flex flex-col items-center">
          {giftCard?.qpay?.QRCode ? (
            <div className="relative inline-block p-3 bg-white rounded-2xl border-2 border-foreground/10">
              <QRCode value={giftCard.qpay.QRCode} size={180} fgColor="#1a1130" bgColor="#ffffff" />
            </div>
          ) : (
            <div className="size-48 rounded-2xl bg-[#F1EEE7] animate-pulse" />
          )}
          <div className="mt-2 text-[11px] text-foreground/55 font-semibold text-center">
            Карт <span className="font-mono text-foreground">#{giftCard?.customId}</span>
          </div>
        </div>
        <div className="flex flex-col">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-foreground/55 mb-1">Төлөх дүн</div>
          <div className="text-[28px] font-bold font-gilroy tracking-[-0.02em] tabular-nums text-foreground">{formatPrice(giftCard?.amount)}</div>
          <ol className="mt-3 flex flex-col gap-2 text-[13px] text-foreground">
            {[
              'Банкны аппаа нээгээд QR уншуулах цэс рүү орно уу',
              'Дээрх QR кодыг уншуулан төлбөрөө баталгаажуулна уу',
            ].map((s, i) => (
              <li key={i} className="flex gap-2.5">
                <span className="shrink-0 size-5 rounded-full bg-[#F1EEE7] text-foreground/60 inline-flex items-center justify-center text-[10px] font-bold mt-0.5">{i + 1}</span>
                <span className="text-foreground/75">{s}</span>
              </li>
            ))}
          </ol>
          <button
            onClick={check}
            disabled={loading}
            className="mt-4 inline-flex items-center justify-center gap-2 h-11 px-5 rounded-full bg-foreground text-cream text-[13.5px] font-semibold hover:bg-foreground/90 disabled:opacity-60 transition-colors cursor-pointer"
          >
            {loading ? 'Шалгаж байна...' : 'Төлбөр шалгах'}
          </button>
        </div>
      </div>

      {giftCard?.qpay?.urls?.length > 0 && (
        <div className="px-6 pb-6 border-t border-foreground/8 pt-4">
          <div className="text-[11px] uppercase tracking-wider text-foreground/55 font-semibold mb-3">Банкны апп шууд нээх</div>
          {isInAppBrowser && (
            <div className="rounded-2xl bg-[#F1EEE7] px-4 py-3.5 mb-3">
              <p className="text-[12.5px] text-foreground/75">{IN_APP_BROWSER_LABELS.message}</p>
              <a href={browserUrl} onClick={openInBrowser} className="mt-3 inline-flex items-center gap-2 justify-center h-10 px-4 rounded-full bg-foreground text-cream text-[13px] font-semibold">
                {IN_APP_BROWSER_LABELS.action} <ExternalLink className="size-3.5" />
              </a>
              <p className="mt-2.5 text-[11.5px] text-foreground/55">{IN_APP_BROWSER_LABELS.hint}</p>
            </div>
          )}
          {!appLinksBlocked && (
            <div className="grid grid-cols-6 sm:grid-cols-8 gap-2">
              {giftCard.qpay.urls.map((b: any, i: number) => (
                <a key={i} href={b.link} className="aspect-square rounded-xl bg-white border border-foreground/10 overflow-hidden hover:border-foreground/30 transition-colors">
                  <img src={b.logo} alt={b.name || `Bank ${i + 1}`} className="w-full h-full object-cover" />
                </a>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function Row({ icon: Icon, label, value }: { icon: any, label: string, value: any }) {
  return (
    <li className="flex items-start gap-3 py-3 first:pt-0 last:pb-0">
      <Icon className="size-3.5 text-foreground/45 mt-1 shrink-0" />
      <div className="flex-1 min-w-0 grid sm:grid-cols-[140px_1fr] gap-0.5 sm:gap-3 items-baseline">
        <div className="text-[12px] text-foreground/55 font-medium">{label}</div>
        <div className="text-[13.5px] font-medium text-foreground wrap-break-word">{value}</div>
      </div>
    </li>
  )
}
